import { SearchFilterTag } from '@/components';
import type {
  AnalysisSearchFormType,
  AnalysisSearchKeys,
} from '@/features/search/_models/analysis-search';
import { t } from '@/i18n';
import { AnalysisTargetLabelModifier } from '@/utils/enum';
import type { ExtractKeyOf } from '@/utils/utils';
import { type FC, useMemo } from 'react';
import { type FieldPathValue, useFormContext, useWatch } from 'react-hook-form';

type FieldKey = (typeof AnalysisSearchKeys)[number];

type HiddenFieldKey = ExtractKeyOf<
  FieldKey,
  | 'isSearchTargetProjectName'
  | 'q'
  | 'nestedExpenditureBudgetPurposeIds'
  | 'nestedOrganizationIds'
  | 'nestedOtherResponsibleOrganizationIds'
  | 'nestedPolicyIds'
  | 'aggregationType'
  | 'aggregationTargetType'
  | 'analysisAxis'
  | 'sheetType'
>;
const hiddenFields: HiddenFieldKey[] = [
  'isSearchTargetProjectName',
  'q',
  'nestedExpenditureBudgetPurposeIds',
  'nestedOrganizationIds',
  'nestedOtherResponsibleOrganizationIds',
  'nestedPolicyIds',
  'aggregationType',
  'aggregationTargetType',
  'analysisAxis',
  // sheetType はルート由来で、ユーザーがフォームから操作する値ではないためタグ非表示
  'sheetType',
];

type Props = { field: FieldKey };
const getValue = <K extends FieldKey>(targetValue: any) =>
  targetValue as FieldPathValue<AnalysisSearchFormType, K>;

export const CommonFilterTag: FC<Props> = ({ field }) => {
  const { setValue, control } = useFormContext<AnalysisSearchFormType>();
  const targetValue = useWatch({ control, name: field });
  const value = useMemo(() => {
    if (!targetValue || hiddenFields.includes(field)) {
      return null;
    }
    switch (field) {
      case 'analysisTarget': {
        const value = getValue<typeof field>(targetValue);
        return `${t('analysis-target')}: ${AnalysisTargetLabelModifier[value]}`;
      }
      case 'fiscalYearRange': {
        const value = getValue<typeof field>(targetValue);
        const startYear = value?.at(0)?.year();
        const endYear = value?.at(1)?.year();
        return `${startYear} - ${endYear}`;
      }
      case 'projectNumbers': {
        const value = getValue<typeof field>(targetValue);
        return `${t('project-number')}: ${value}`;
      }
      default:
        return field;
    }
  }, [field, targetValue]);
  if (!value) {
    return null;
  }
  return (
    <SearchFilterTag
      label={value}
      closable={field !== 'fiscalYearRange' && field !== 'analysisTarget'}
      onClose={() => {
        setValue(field, null as any);
      }}
    />
  );
};
